<template>
  <div class="panel-rutas">
    <div class="barra">
      <v-text-field
        v-model="filtroTexto"
        label="Buscar por nombre, ciudad o descripción"
        prepend-inner-icon="mdi-magnify"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        @keydown.enter="buscar"
        @click:clear="limpiarBusqueda"
      />
      <v-btn
        color="primary"
        variant="tonal"
        @click="buscar"
      >
        Buscar
      </v-btn>
      <v-btn
        color="primary"
        prepend-icon="mdi-plus"
        @click="abrirDialogo()"
      >
        Nueva ruta
      </v-btn>
    </div>

    <div
      v-if="cargando"
      class="estado"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <div
      v-else-if="rutas.length === 0"
      class="estado"
    >
      <v-icon
        size="48"
        color="grey-lighten-1"
      >
        mdi-map-marker-path
      </v-icon>
      <span v-if="filtroTexto">No se encontró ninguna ruta con esa búsqueda.</span>
      <span v-else>Todavía no hay rutas. Crea la primera y agrégale paradas.</span>
    </div>

    <div
      v-else
      class="lista"
    >
      <v-card
        v-for="ruta in rutas"
        :key="ruta.idRoute"
        class="ruta"
        variant="outlined"
      >
        <div class="ruta-fila">
          <div class="ruta-texto">
            <div class="ruta-nombre">
              {{ ruta.name }}
              <span
                class="estado-chip"
                :class="ruta.active ? 'chip-activa' : 'chip-apagada'"
              >
                {{ ruta.active ? 'Activa' : 'Desactivada' }}
              </span>
            </div>
            <div
              v-if="ruta.city || ruta.department"
              class="ruta-ubicacion"
            >
              <v-icon size="14">mdi-map-marker</v-icon>
              {{ [ruta.city, ruta.department].filter(Boolean).join(', ') }}
            </div>
            <p
              v-if="ruta.description"
              class="ruta-descripcion"
            >
              {{ ruta.description }}
            </p>
            <code class="ruta-slug">/ruta/{{ ruta.slug }}</code>
          </div>

          <div class="ruta-acciones">
            <v-btn
              size="small"
              variant="tonal"
              :color="ruta.active ? 'grey' : 'green'"
              :loading="activando === ruta.idRoute"
              @click="alternarActiva(ruta)"
            >
              <v-icon start size="16">
                {{ ruta.active ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}
              </v-icon>
              {{ ruta.active ? 'Desactivar' : 'Activar' }}
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              @click="abrirDialogo(ruta)"
            >
              <v-icon start size="16">
                mdi-pencil
              </v-icon>
              Editar
            </v-btn>
            <v-btn
              size="small"
              variant="tonal"
              color="red"
              @click="pedirConfirmacion(ruta)"
            >
              <v-icon start size="16">
                mdi-delete
              </v-icon>
              Eliminar
            </v-btn>
          </div>
        </div>

        <!-- Paradas e imagenes se cargan solo al abrir: una lista de rutas no
             tiene por que pedir el contenido de todas de una. -->
        <div class="ruta-paradas">
          <v-btn
            variant="text"
            size="small"
            class="btn-desplegar"
            @click="alternarParadas(ruta.idRoute)"
          >
            <v-icon start size="18">
              {{ abierta === ruta.idRoute ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
            {{ abierta === ruta.idRoute ? 'Ocultar contenido' : 'Ver y editar contenido' }}
          </v-btn>

          <template v-if="abierta === ruta.idRoute">
            <GaleriaEditable
              :imagenes="imagenesRuta"
              titulo="Imágenes de la ruta"
              ayuda="La primera es la portada. Sin ninguna, la tarjeta usa la foto de la primera parada."
              class="mt-2"
              @agregar="dialogoImagen = true"
              @eliminar="eliminarImagen"
            />

            <v-divider class="my-4" />

            <PanelParadasRuta :id-ruta="ruta.idRoute" />
          </template>
        </div>
      </v-card>

      <v-pagination
        v-if="totalPaginas > 1"
        v-model="paginaVisible"
        :length="totalPaginas"
        :total-visible="5"
        density="comfortable"
        active-color="primary"
        class="mt-3"
        @update:model-value="cambiarPagina"
      />
    </div>

    <!-- ALTA Y EDICION -->
    <v-dialog
      v-model="dialogo"
      max-width="600"
    >
      <v-card class="modal">
        <div class="modal-cabecera">
          <div>
            <div class="modal-etiqueta">
              <v-icon size="12">
                mdi-map-marker-path
              </v-icon>
              {{ form.idRoute ? 'Editando' : 'Nueva' }}
            </div>
            <h2 class="modal-titulo">
              {{ form.idRoute ? 'Editar ruta' : 'Nueva ruta' }}
            </h2>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="modal-cerrar"
            @click="dialogo = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="modal-cuerpo">
          <div class="modal-grupo">
            Datos básicos
          </div>
          <v-text-field
            v-model="form.name"
            :error-messages="errorNombre"
            class="fg-blue"
            label="Nombre"
            variant="outlined"
            density="compact"
            maxlength="150"
            hint="Ej: Desayuno en Chía"
          />
          <v-textarea
            v-model="form.description"
            class="fg-blue mt-2"
            label="Descripción"
            variant="outlined"
            density="compact"
            rows="3"
            auto-grow
            :counter="2000"
            maxlength="2000"
            hint="De qué va el recorrido y cómo tomarlo. Aquí va lo que no es una parada: tiempos, consejos, cómo moverse."
            persistent-hint
          />

          <div class="modal-grupo mt-5">
            Dónde
          </div>
          <div class="modal-grid-2">
            <v-text-field
              v-model="form.city"
              class="fg-blue"
              label="Ciudad"
              variant="outlined"
              density="compact"
              maxlength="120"
            />
            <v-text-field
              v-model="form.department"
              class="fg-blue"
              label="Departamento"
              variant="outlined"
              density="compact"
              maxlength="120"
            />
          </div>

          <p
            v-if="!form.idRoute"
            class="modal-ayuda"
          >
            La ruta se crea desactivada. Agrégale las paradas y actívala cuando
            esté lista.
          </p>

          <v-alert
            v-if="errorForm"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mt-4"
          >
            {{ errorForm }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="modal-pie">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="guardando"
            @click="dialogo = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="guardando"
            @click="guardar"
          >
            <v-icon start>
              mdi-content-save
            </v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="dialogoImagen"
      max-width="620"
      :persistent="subiendoImagenes"
    >
      <div style="text-align: -webkit-center; width: 100%;">
        <EntryLoalImage
          ref="entradaImagenes"
          multiple
          :subiendo="subiendoImagenes"
          :progreso="progresoSubida"
          @onImageSelected="subirImagenes"
        />
      </div>
    </v-dialog>

    <!-- CONFIRMAR BORRADO -->
    <v-dialog
      v-model="dialogoEliminar"
      max-width="420"
      :persistent="eliminando"
    >
      <v-card class="modal modal-confirmar">
        <div class="modal-confirmar-cabecera">
          <div class="modal-confirmar-icono">
            <v-icon size="20">
              mdi-alert-outline
            </v-icon>
          </div>
          <h2 class="modal-confirmar-titulo">
            Eliminar ruta
          </h2>
        </div>
        <v-card-text class="modal-confirmar-cuerpo">
          <p v-if="porEliminar">
            Se va a eliminar <strong>{{ porEliminar.name }}</strong> y todas sus paradas.
          </p>
          <p class="modal-irreversible">
            Esta acción no se puede deshacer.
          </p>
        </v-card-text>
        <v-card-actions class="modal-confirmar-pie">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="eliminando"
            @click="dialogoEliminar = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="red"
            variant="flat"
            :loading="eliminando"
            @click="eliminar"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="aviso"
      :timeout="colorAviso === 'error' ? -1 : 4000"
      :color="colorAviso"
      location="top"
    >
      {{ mensajeAviso }}
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="aviso = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import apisRutas from '@/apis/apisRutas'
import PanelParadasRuta from './PanelParadasRuta.vue'
import GaleriaEditable from '@/components/Fields/GaleriaEditable.vue'
import EntryLoalImage from '@/components/Fields/EntryLoalImage.vue'
import { mensajeDeError } from '@/utils/errores'

/**
 * Rutas, para el administrador.
 *
 * Vive dentro de la pantalla de lugares porque una ruta es contenido curado del
 * mismo tipo: junta lugares y empresas en un recorrido.
 */
export default {
  name: 'PanelRutas',
  components: { PanelParadasRuta, GaleriaEditable, EntryLoalImage },
  data() {
    return {
      rutas: [],
      cargando: true,
      filtroTexto: '',
      pagina: 0,
      totalPaginas: 1,

      // Solo una ruta desplegada a la vez: con varias abiertas la pantalla se
      // vuelve una lista de listas y se pierde cual es cual
      abierta: null,
      // Las de la ruta desplegada. Se vacian al cerrar, asi no hay forma de
      // enseñar las de una ruta dentro de otra.
      imagenesRuta: [],
      dialogoImagen: false,
      subiendoImagenes: false,
      progresoSubida: '',

      dialogo: false,
      form: this.formVacio(),
      intentoGuardar: false,
      guardando: false,
      errorForm: null,

      dialogoEliminar: false,
      porEliminar: null,
      eliminando: false,

      activando: null,

      aviso: false,
      mensajeAviso: '',
      colorAviso: 'success'
    }
  },
  computed: {
    /** v-pagination cuenta desde 1 y el backend desde 0. */
    paginaVisible: {
      get() {
        return this.pagina + 1
      },
      set(valor) {
        this.pagina = valor - 1
      }
    },
    errorNombre() {
      if (!this.intentoGuardar) {
        return []
      }
      return this.form.name && this.form.name.trim() ? [] : ['El nombre es obligatorio']
    }
  },
  created() {
    this.cargar()
  },
  methods: {
    formVacio() {
      return {
        idRoute: null,
        name: '',
        description: '',
        city: '',
        department: ''
      }
    },
    buscar() {
      this.pagina = 0
      this.cargar()
    },
    limpiarBusqueda() {
      this.filtroTexto = ''
      this.buscar()
    },
    cargar() {
      this.cargando = true
      this.abierta = null

      apisRutas.buscar(this.filtroTexto || '', this.pagina)
        .then(respuesta => {
          this.rutas = respuesta.content || []
          this.totalPaginas = respuesta.totalPages || 1
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudieron cargar las rutas'), 'error')
        })
        .finally(() => {
          this.cargando = false
        })
    },
    cambiarPagina() {
      this.cargar()
    },
    alternarParadas(idRuta) {
      this.abierta = this.abierta === idRuta ? null : idRuta
      this.imagenesRuta = []

      if (this.abierta) {
        this.cargarImagenes()
      }
    },
    cargarImagenes() {
      apisRutas.imagenes(this.abierta)
        .then(imagenes => {
          this.imagenesRuta = imagenes || []
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudieron cargar las imágenes'), 'error')
        })
    },
    /**
     * Una a una y en serie, no todas a la vez: cada imagen viaja en base64 y
     * son peticiones grandes; en paralelo se atropellan y el progreso deja de
     * significar nada.
     */
    async subirImagenes(imagenes) {
      const lista = Array.isArray(imagenes) ? imagenes : [imagenes]
      const idRuta = this.abierta

      if (lista.length === 0 || !idRuta) {
        return
      }

      this.subiendoImagenes = true

      try {
        for (let i = 0; i < lista.length; i++) {
          this.progresoSubida = `Subiendo ${i + 1} de ${lista.length}...`
          await apisRutas.guardarImagen(idRuta, this.soloBase64(lista[i]))
        }

        // Vaciar el selector es parte de guardar, no un detalle: si no, al
        // volver a abrir Agregar sigue ahi la foto anterior con su "1 imagen
        // lista", parece que no se guardo nada, y darle a Subir otra vez la
        // sube duplicada. Lo mismo hacen la pantalla de lugares y la de
        // editar empresa.
        if (this.$refs.entradaImagenes) {
          this.$refs.entradaImagenes.limpiar()
        }

        this.avisar('Imágenes guardadas', 'success')
        this.cargarImagenes()
      } catch (error) {
        this.avisar(mensajeDeError(error, 'No se pudo guardar la imagen'), 'error')
      } finally {
        this.subiendoImagenes = false
        this.progresoSubida = ''
        this.dialogoImagen = false
      }
    },
    /** El backend decodifica base64 puro; el prefijo data: lo rompe. */
    soloBase64(valor) {
      const texto = String(valor)
      const coma = texto.indexOf(',')

      return coma === -1 ? texto : texto.slice(coma + 1)
    },
    eliminarImagen(idImagen) {
      apisRutas.eliminarImagen(idImagen)
        .then(() => {
          this.avisar('Imagen eliminada', 'success')
          this.cargarImagenes()
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudo eliminar la imagen'), 'error')
        })
    },
    abrirDialogo(ruta = null) {
      this.intentoGuardar = false
      this.errorForm = null

      this.form = ruta
        ? {
          idRoute: ruta.idRoute,
          name: ruta.name || '',
          description: ruta.description || '',
          city: ruta.city || '',
          department: ruta.department || ''
        }
        : this.formVacio()

      this.dialogo = true
    },
    guardar() {
      this.intentoGuardar = true
      this.errorForm = null

      if (this.errorNombre.length) {
        return
      }

      this.guardando = true

      apisRutas.guardar({
        idRoute: this.form.idRoute,
        name: this.form.name.trim(),
        description: this.form.description ? this.form.description.trim() : null,
        city: this.form.city ? this.form.city.trim() : null,
        department: this.form.department ? this.form.department.trim() : null
      })
        .then(() => {
          this.dialogo = false
          this.avisar('Ruta guardada', 'success')
          this.cargar()
        })
        .catch(error => {
          this.errorForm = mensajeDeError(error, 'No se pudo guardar la ruta')
        })
        .finally(() => {
          this.guardando = false
        })
    },
    alternarActiva(ruta) {
      this.activando = ruta.idRoute

      apisRutas.activar(ruta.idRoute, !ruta.active)
        .then(actualizada => {
          // Se reemplaza con lo que devolvio el servidor y no con un toggle
          // local: si el guardado falla a medias, la pantalla no miente
          const indice = this.rutas.findIndex(r => r.idRoute === ruta.idRoute)

          if (indice !== -1) {
            this.rutas.splice(indice, 1, actualizada)
          }
          this.avisar(actualizada.active ? 'Ruta activada' : 'Ruta desactivada', 'success')
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudo cambiar el estado'), 'error')
        })
        .finally(() => {
          this.activando = null
        })
    },
    pedirConfirmacion(ruta) {
      this.porEliminar = ruta
      this.dialogoEliminar = true
    },
    eliminar() {
      if (!this.porEliminar) {
        return
      }

      this.eliminando = true

      apisRutas.eliminar(this.porEliminar.idRoute)
        .then(() => {
          this.dialogoEliminar = false
          this.avisar('Ruta eliminada', 'success')
          this.cargar()
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudo eliminar la ruta'), 'error')
        })
        .finally(() => {
          this.eliminando = false
          this.porEliminar = null
        })
    },
    avisar(mensaje, color) {
      this.mensajeAviso = mensaje
      this.colorAviso = color
      this.aviso = true
    }
  }
}
</script>

<style scoped>
/* Mobile first: la barra apila y los botones ocupan el ancho */
.barra {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}
@media (min-width: 700px) {
  .barra {
    flex-direction: row;
    align-items: center;
  }
}

.lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ruta {
  padding: 14px;
  border-radius: 12px;
}
.ruta-fila {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
@media (min-width: 760px) {
  .ruta-fila {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
}

.ruta-texto {
  min-width: 0;
}
.ruta-nombre {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 1rem;
  font-weight: 700;
  color: #1a1a1a;
}
.estado-chip {
  padding: 1px 8px;
  border-radius: 9px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.chip-activa {
  background: #e6f5ee;
  color: #0f7a52;
}
.chip-apagada {
  background: #f2f5f9;
  color: #667085;
}
.ruta-ubicacion {
  display: flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  font-size: 0.78rem;
  color: #98a2b3;
}
.ruta-descripcion {
  margin: 6px 0 0;
  max-width: 620px;
  font-size: 0.84rem;
  line-height: 1.45;
  color: #667085;
}
.ruta-slug {
  display: inline-block;
  margin-top: 8px;
  padding: 1px 7px;
  border-radius: 5px;
  background: #f2f5f9;
  color: #475467;
  font-size: 0.72rem;
  word-break: break-all;
}

.ruta-acciones {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.ruta-paradas {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #eceff1;
}
.btn-desplegar {
  text-transform: none;
  letter-spacing: 0;
}

.estado {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 56px 12px;
  color: #667085;
  font-size: 0.9rem;
  text-align: center;
}
</style>
